
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Alert from '~/components/Alerts/Alert.vue';
import ConfirmPaymentDialog from '@/components/common/Dialogs/ConfirmPaymentDialog.vue';
import CustomButton from '~/components/Buttons/CustomButton.vue';
import CurrencyInput from '~/components/Inputs/Currency.vue';
import { formattedDate } from '@/utils/dates';
import { getLoanInfo } from '@/api/loan';
import { PaymentProviderBankDetails } from '@/interfaces/bank-account';
import { LoanDetailsData } from '@/interfaces/loan';

@Component({
  components: {
    Alert,
    ConfirmPaymentDialog,
    CustomButton,
    CurrencyInput
  }
})
export default class OneTimePayment extends Vue {
  @Prop(Boolean) readonly isVisible!: boolean;

  loanData: LoanDetailsData | null = null;

  paymentAmount = '';

  showPaymentForm = false;

  showPaymentConfirmation = false;

  loading = false;

  loadingPage = false;

  success = false;

  showPaymentInformation = false;

  valid = false;

  paymentId = '';

  selectedPaymentOption = {
    id: '',
    type: '',
    lastFour: ''
  };

  get buttonDisabled() {
    return !this.paymentAmount.length || !this.valid;
  }

  get date() {
    return formattedDate();
  }

  get bankAccount(): PaymentProviderBankDetails {
    return this.$store.getters['PaymentOptions/getBankAccount'];
  }

  get card() {
    return this.$store.getters['PaymentOptions/getCard'];
  }

  get paymentMethods() {
    const options = [];

    if (this.bankAccount && this.$store.getters['PaymentOptions/bankAccountIsVerified']) {
      options.push({
        id: this.bankAccount.id,
        type: 'bank',
        text: `Bank account ending in ${this.bankAccount.bank_account_last_4}`,
        lastFour: this.bankAccount.bank_account_last_4
      });
    }

    if (this.card) {
      options.push({
        id: this.card.id,
        type: 'card',
        text: `Debit card ending in ${this.card.card_last_4}`,
        lastFour: this.card.card_last_4
      });
    }

    return options;
  }

  get amountDue(): string {
    return `$${this.loanData?.loan_details?.amount_due.toFixed(2) ?? '0.00'}`;
  }

  get payoffAmount(): string {
    return `$${this.loanData?.loan_details?.pay_off_amount.toFixed(2)}`;
  }

  get getBrandName(): string {
    return this.$store.getters['Config/getBrandName'];
  }

  get getBrandLegalName(): string {
    return this.$store.getters['Config/getLegalBusinessName'];
  }

  get supportEmail() {
    return this.$store.getters['Config/getBrandSupportEmail'];
  }

  get supportPhone() {
    return this.$store.getters['Config/getBrandSupportPhone'];
  }

  async created() {
    if (this.isVisible) await this.fetchLoan();
  }

  mounted() {
    this.selectedPaymentOption = this.paymentMethods[0];
  }

  showConfirmationDialog() {
    this.showPaymentConfirmation = true;
  }

  submitPayment(paymentId: string) {
    this.paymentId = paymentId;
    this.showPaymentConfirmation = false;
    this.success = true;
  }

  private async fetchLoan() {
    this.loadingPage = true;
    try {
      const { data } = await getLoanInfo(this.loanNumber);
      this.loanData = data;
    } finally {
      this.loadingPage = false;
    }
  }

  get loanNumber() {
    return this.$store.getters['User/activeLoanNumber'];
  }

  @Watch('paymentMethods')
  onPaymentMethodsUpdate() {
    if (this.paymentMethods.length) {
      this.selectedPaymentOption = this.paymentMethods[0];
    } else {
      this.selectedPaymentOption = {
        id: '',
        type: '',
        lastFour: ''
      };
    }
  }
}
